import { memo } from 'react';
import { MdErrorOutline } from 'react-icons/md';
import { formatDetailedDateTime } from '@/utils/chatDateTime';
import { ChatAIAvatar } from '../ChatAIAvatar/ChatAIAvatar';
import { ChatAIMessage } from '../ChatAIMessage/ChatAIMessage';
import ChatMessageMarkdown from '../Form/ChatMessageMarkdown';
import ChatToolBar from '../Form/ChatToolBar';
import type { ChatAIFormMessage } from '../Form/ChatAIFormMessage';

interface ChatAIFeedEntryProps {
    message: ChatAIFormMessage;
    assistantName: string;
}

function ChatAIFeedEntryView({ message, assistantName }: ChatAIFeedEntryProps) {
    const detailedTime = message.sentAt ? formatDetailedDateTime(message.sentAt) : undefined;
    if (message.direction === 'outgoing') {
        return (
            <ChatAIMessage direction="outgoing" author="Вы" sentTime={message.sentTime} sentTimeTitle={detailedTime}>
                {message.message}
            </ChatAIMessage>
        );
    }
    return (
        <ChatAIMessage
            direction="incoming"
            author={message.sender}
            sentTime={message.sentTime}
            sentTimeTitle={detailedTime}
            avatar={<ChatAIAvatar inMessage error={message.isError} />}
            avatarLabel={assistantName}
            error={message.isError}
            // Оценка и копирование для сбоя смысла не имеют.
            actions={!message.isError && <ChatToolBar message={message} />}
        >
            {message.isError ? (
                <div className="chat-ai-error-answer">
                    <MdErrorOutline className="chat-ai-error-answer__icon" role="img" aria-label="Ошибка" />
                    <ChatMessageMarkdown content={message.message} />
                </div>
            ) : (
                <ChatMessageMarkdown content={message.message} />
            )}
        </ChatAIMessage>
    );
}

// По всем ключам, а не по списку полей: новое поле сообщения без правки сравнения тоже перерисует запись.
const sameMessage = (a: ChatAIFormMessage, b: ChatAIFormMessage) => {
    if (a === b) return true;
    const keys = Object.keys(a) as (keyof ChatAIFormMessage)[];
    return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
};

/**
 * Сообщение ленты. Сравнение по полям, а не по ссылке: история из стора на каждое обновление треда собирает новые
 * объекты сообщений, а лента перерисовывается на статус соединения, ввод и ответы — без memo каждое сообщение заново
 * разбирало markdown и перерисовывало аватар.
 */
export const ChatAIFeedEntry = memo(
    ChatAIFeedEntryView,
    (prev, next) => prev.assistantName === next.assistantName && sameMessage(prev.message, next.message),
);
